import React, { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReportCard from '../../analytics/healthAnalytics/components/ReportCard';
import InsightList from '../../analytics/healthAnalytics/components/InsightList';

export const fmtCount = (value) => (value === null || value === undefined ? '—' : Number(value).toLocaleString('en-IN'));
export const fmtPct = (value) => (value === null || value === undefined ? '—' : `${Number(value)}%`);
export const SMALL_GROUP = 'Fewer than 5 people: hidden';
export const SITE_TONES = ['bg-ds-brand', 'bg-ds-amber', 'bg-ds-green', 'bg-ds-purple', 'bg-gray-400'];
// Distinct colours for sites in a chart; past these, sites are merged into OTHER_COLOR
export const SITE_COLORS = ['#2563EB', '#D97706', '#14866D', '#7C3AED', '#E8432C', '#0EA5E9', '#14B8A6', '#1B4965'];
export const OTHER_COLOR = '#CBD5E1';

// Hover tooltips that show at once and sit above everything (a card's or a scroll area's edge can't
// cut them). TipLayer wraps the page; tip(text) gives the mouse handlers for one bar, segment or cell.
const TipContext = createContext(null);

export const TipLayer = ({ children }) => {
  const [tip, setTip] = useState(null);
  const tipRef = useRef(null);
  useLayoutEffect(() => {
    if (!tip || !tipRef.current) return;
    const node = tipRef.current;
    const rect = node.getBoundingClientRect();
    const x = Math.max(10, Math.min(tip.x + 16, window.innerWidth - rect.width - 10));
    const above = tip.y - rect.height - 14;
    const y = Math.max(10, Math.min(above < 10 ? tip.y + 20 : above, window.innerHeight - rect.height - 10));
    node.style.left = `${x}px`;
    node.style.top = `${y}px`;
  }, [tip]);
  return (
    <TipContext.Provider value={setTip}>
      {children}
      {tip ? createPortal(
        <div ref={tipRef} role='tooltip' className='pointer-events-none fixed z-[1000] max-w-[280px] rounded-ds-sm bg-gray-900 px-[10px] py-[6px] text-[12px] text-white shadow-lg'
          style={{ left: tip.x + 12, top: tip.y + 14 }}>
          {tip.text}
        </div>, document.body) : null}
    </TipContext.Provider>
  );
};

// Without a TipLayer (tests, PNG export) the text falls back to the browser's own title
export const useTip = () => {
  const setTip = useContext(TipContext);
  return (text) => (setTip ? {
    onMouseEnter: (e) => setTip({ text, x: e.clientX, y: e.clientY }),
    onMouseMove: (e) => setTip({ text, x: e.clientX, y: e.clientY }),
    onMouseLeave: () => setTip(null),
    'aria-label': text,
  } : { title: text });
};

// The first `limit` rows, and a button to show the rest in a scrolling list
export const ShowMore = ({ items, limit = 8, noun = 'sites', render }) => {
  const [open, setOpen] = useState(false);
  const shown = open ? items : items.slice(0, limit);
  return (
    <>
      <div className={open && items.length > limit ? 'max-h-[360px] overflow-y-auto pr-1' : ''}>{shown.map(render)}</div>
      {items.length > limit ? (
        <button type='button' onClick={() => setOpen(!open)} className='mt-ds-3 text-[12px] font-semibold text-ds-brand'>
          {open ? `Show the top ${limit}` : `Show all ${items.length} ${noun}`}
        </button>
      ) : null}
    </>
  );
};

// Text from the page's writer marks the numbers to highlight with **double asterisks**.
export const Marked = ({ text = '' }) => (
  <>
    {String(text).split(/(\*\*[^*]+\*\*)/).map((part, i) => (part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} className='font-semibold text-gray-900'>{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>))}
  </>
);

export const Overline = ({ children, className = 'text-gray-600' }) => (
  <div className={`text-ts-2xs font-semibold uppercase tracking-[0.12em] ${className}`}>{children}</div>
);

// A chart card that can save itself as a PNG, like Health analytics' cards
export const ChartCard = ({ fileName, title, subtitle, children, className = '' }) => (
  <ReportCard fileName={fileName} className={`p-[26px] ${className}`}>
    <h3 className='m-0 font-fig-extrabold text-[22px] font-bold leading-[1.2] text-gray-900'>{title}</h3>
    {subtitle ? <p className='mb-ds-6 mt-[6px] text-xs text-gray-600'>{subtitle}</p> : <div className='mb-ds-6' />}
    {children}
  </ReportCard>
);

// "What the numbers say": at most three numbered findings
export const NumbersSay = ({ bullets }) => (bullets && bullets.length ? (
  <div className='rounded-ds-lg border border-gray-300 bg-white p-[26px]'>
    <Overline className='mb-ds-4 text-ds-brand'>What the numbers say</Overline>
    <InsightList divided items={bullets.map((bullet, i) => <Marked key={i} text={bullet} />)} />
  </div>
) : null);

export const Legend = ({ items }) => (
  <div className='mb-ds-4 flex flex-wrap gap-ds-4 text-[12px] text-gray-700'>
    {items.map(({ label, tone, color }) => (
      <span key={label} className='inline-flex items-center gap-[6px]'>
        <span className={`h-[10px] w-[10px] rounded-[3px] ${tone || ''}`} style={color ? { background: color } : undefined} />{label}
      </span>
    ))}
  </div>
);

// A labelled horizontal bar: value as a share of max, with an optional note under the label
export const BarRow = ({ label, value, max, display, note, tone = 'bg-ds-brand', title, labelWidth = 140 }) => {
  const tip = useTip();
  return (
  <div className='grid items-center gap-x-ds-3 gap-y-1 py-[6px]' style={{ gridTemplateColumns: `${labelWidth}px minmax(0,1fr) 72px` }}
    {...tip([`${label}: ${display}`, note, title].filter(Boolean).join(' · '))}>
    <span className='truncate text-[13px] font-semibold text-gray-800'>{label}</span>
    <span className='h-[14px] overflow-hidden rounded-ds-sm bg-gray-100'>
      {value === null || value === undefined ? null : (
        <span className={`block h-full rounded-ds-sm ${tone}`} style={{ width: `${max ? Math.max(1.5, Math.min(100, (100 * value) / max)) : 0}%` }} />
      )}
    </span>
    <span className='text-right font-jetbrains-mono text-[12px] font-semibold text-gray-900'>{display}</span>
    {note ? <span className='text-[11px] text-gray-500' style={{ gridColumn: '1 / 4' }}>{note}</span> : null}
  </div>
  );
};

// Vertical columns over a sequence (months, age bands). Each column is one value, or segments stacked
// bottom-up; the total sits on top and the highlighted column stands out.
// Hovering a column (or one segment of it) names it with its value.
// A column's id (its label unless given) keys it and is what `highlight` names, so two columns can share a label
export const Columns = ({ columns, highlight, plotHeight = 200 }) => {
  const tip = useTip();
  const id = (c) => c.id ?? c.label;
  const max = Math.max(...columns.map((c) => c.value || 0), 1);
  return (
    <div className='grid gap-x-[10px] gap-y-ds-3'
      style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`, gridTemplateRows: `auto ${plotHeight}px auto` }}>
      {columns.map((c) => (
        <div key={`v-${id(c)}`} className={`text-center font-jetbrains-mono text-[13px] font-semibold tabular-nums ${id(c) === highlight ? 'text-ds-brand-dk' : 'text-gray-800'}`}
          {...tip(c.title)}>
          {c.display}
        </div>
      ))}
      {columns.map((c) => (
        <div key={`b-${id(c)}`} className='flex flex-col-reverse self-end overflow-hidden rounded-t-[6px]'
          style={{ height: `${c.value ? Math.max(2, (100 * c.value) / max) : 0}%` }} {...(c.segments ? {} : tip(c.title))}>
          {(c.segments || [{ value: c.value, tone: id(c) === highlight ? 'bg-ds-brand' : 'bg-ds-brand-200' }]).filter((s) => s.value > 0).map((s, i) => (
            <span key={i} className={`block ${s.tone || ''}`} style={{ height: `${(100 * s.value) / c.value}%`, background: s.color }}
              {...(s.title ? tip(s.title) : {})} />
          ))}
        </div>
      ))}
      {columns.map((c) => (
        <div key={`a-${id(c)}`} className='border-t border-gray-300 pt-[8px] text-center font-jetbrains-mono text-[11px] font-semibold text-gray-700'>
          {c.label}
        </div>
      ))}
    </div>
  );
};

// One share bar split into labelled segments, with a legend
export const ShareBar = ({ segments }) => {
  const tip = useTip();
  const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;
  return (
    <div>
      <div className='flex h-[14px] overflow-hidden rounded-ds-sm'>
        {segments.filter((s) => s.value > 0).map((s) => (
          <span key={s.label} className={s.tone} style={{ width: `${(100 * s.value) / total}%` }}
            {...tip(`${s.label}: ${fmtCount(s.value)} (${Math.round((100 * s.value) / total)}%)`)} />
        ))}
      </div>
      <div className='mt-ds-3 flex flex-wrap gap-x-ds-5 gap-y-1'>
        {segments.map((s) => (
          <span key={s.label} className='inline-flex items-center gap-[6px] text-[12px] text-gray-700'>
            <span className={`h-[10px] w-[10px] rounded-[3px] ${s.tone}`} />
            {s.label} <span className='font-jetbrains-mono font-semibold text-gray-900'>{fmtCount(s.value)}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export const Tile = ({ value, label, description, title, tone = 'text-gray-900' }) => (
  <div className='rounded-ds-lg border border-gray-300 bg-white px-[22px] pb-ds-5 pt-[22px]'>
    <div className={`font-jetbrains-mono text-[30px] font-semibold leading-none tracking-[-0.02em] ${tone}`} title={title}>{value}</div>
    <div className='mt-[14px] text-[13px] font-semibold text-gray-900'>{label}</div>
    <div className='mt-1 text-[12px] text-gray-600'>{description}</div>
  </div>
);
